import React from "react"
import {useTranslation} from "react-i18next"

import {GenericErrorAlert} from "../../../components/Alert.tsx"
import Button from "../../../components/Button.tsx"
import {Flyout} from "../../../components/Flyout.tsx"
import {Loading} from "../../../components/Loading.tsx"
import {TabItem, TabsContainer} from "../../../components/Tabs.tsx"
import {i18n} from "../../../i18n.ts"
import {useSalesAssistantDealDetailQuery} from "../../../queries/salesAssistant.ts"
import {ASalesAssistantProspect} from "../../../services/types.generated.ts"
import {SalesAssistantDealDetailContext} from "../hooks.ts"
import {ActivityInfo} from "./ActivityInfo.tsx"
import {DealNotes} from "./DealNotes.tsx"
import {ProspectInfo} from "./ProspectInfo.tsx"

type TTab = "details" | "activities" | "deal-history"
export type TTabComponentProps = {prospect: ASalesAssistantProspect}
const tabs = [
  {tab: "details", title: i18n.t("SalesAssistant_Deals_Detail_Details"), component: ProspectInfo},
  {tab: "activities", title: i18n.t("SalesAssistant_Deals_Detail_Activities"), component: ActivityInfo},
  {tab: "deal-history", title: i18n.t("SalesAssistant_Deals_Detail_DealHistory"), component: DealNotes},
] as const satisfies Array<{tab: TTab; title: string; component: React.ComponentType<TTabComponentProps>}>

export const DealDetailFlyout: React.FC = () => {
  const {t} = useTranslation()
  const {value, setValue} = SalesAssistantDealDetailContext.useContext()

  const {data, isFetching, error, refetch} = useSalesAssistantDealDetailQuery({
    externalId: value?.external_id ?? undefined,
  })

  const prospect = data?.data.prospect
  const handleClose = React.useCallback(() => {
    setValue(null)
  }, [setValue])

  const isOpen = value != null
  const [currentTab, setCurrentTab] = React.useState<TTab>("details")
  const TabComponent = React.useMemo(() => {
    return tabs.find(({tab}) => tab === currentTab)?.component ?? (() => null)
  }, [currentTab])

  return (
    <Flyout
      isOpen={isOpen}
      onClose={handleClose}
      title={
        <div className={"flex flex-col gap-2 font-normal"}>
          <span className={"font-bold"}>{prospect?.organization_name}</span>
          <span className={"text-xs"}>#{prospect?.id}</span>
        </div>
      }
      buttons={
        <div className={"flex w-full justify-end"}>
          <Button variant={"outlined"} onClick={handleClose}>
            {t("SalesAssistant_Deals_Detail_Cancel")}
          </Button>
        </div>
      }
    >
      <TabsContainer className={"px-6"} size={"sm"}>
        {tabs.map(({title, tab}) => (
          <TabItem key={tab} onClick={() => setCurrentTab(tab)} isActive={tab === currentTab}>
            {title}
          </TabItem>
        ))}
      </TabsContainer>
      {error ? (
        <GenericErrorAlert retry={refetch} />
      ) : isFetching || !prospect ? (
        <Loading />
      ) : (
        <TabComponent prospect={prospect} />
      )}
    </Flyout>
  )
}
